<template>
  <div :class="bem()">
    <div :class="bem('product-section')">
      <div :class="bem('product-select')">
        <TechOption
          v-for="product in caseOptions.products"
          :key="product"
          :option="{
            active: chosenProduct === product,
            icon: getProductIcon(product),
            iconSize: 'x-large',
            label: getProductName(product, false),
            inStock: true,
          }"
          grey
          :invert-active-icon="false"
          :class="bem('product-option')"
          @click="() => (chosenProduct = product)"
        />
      </div>
    </div>

    <div
      v-if="caseOptions.compactAvailable"
      :class="bem('case-type-section')"
    >
      <div :class="bem('case-type-select')">
        <TechOption
          :option="{
            active: caseStyle === 'standard',
            label: 'Capsule Case',
            inStock: true,
          }"
          grey
          :class="bem('case-type-option')"
          @click="() => (caseStyle = 'standard')"
        />
        <TechOption
          :option="{
            active: caseStyle === 'compact',
            label: 'Compact Case',
            inStock: true,
          }"
          grey
          :class="bem('case-type-option')"
          @click="() => (caseStyle = 'compact')"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import CustomisedProduct from '~/models/CustomisedProduct'

const props = defineProps({
  customisedProduct: { type: CustomisedProduct, required: true },
})

const bem = useBEM('CaseCustomiser')

const { $product } = useNuxtApp()
const { resolveLocalePath } = useUrlBuilder()

const store = useProductStore()
const {
  icon: getProductIcon,
  productName: getProductName
} = storeToRefs(store)

const chosenProduct = ref(null)
const caseStyle = ref(null)
const allCaseOptions = [{
  products: ['vanguards', 'snipers', 'ullrs'],
  compactAvailable: true,
  mapping: {
    vanguards: {
      compact: 'case-ct_compactvanguards',
      standard: 'case-ct_revoltcapsule',
    },
    ullrs: {
      compact: 'case-ct_compactvanguards',
      standard: 'case-ct_revoltcapsule',
    },
    snipers: {
      compact: 'case-ct_compactsnipers',
      standard: 'case-ct_revoltcapsule',
    },
  },
}, {
  products: ['vulcans', 'airas', 'velans', 'ultras', 'gts'],
  mapping: {
    vulcans: { standard: 'case-ct_pacecapsule' },
    airas: { standard: 'case-ct_pacecapsule' },
    ultras: { standard: 'case-ct_pacecapsule' },
    velans: { standard: 'case-ct_pacecapsule' },
    gts: { standard: 'case-ct_pacecapsule' },
  },
}, {
  products: [
    'classics4',
    'renegades',
    'sierras',
    'tempests',
    'zephyrs',
    'tokas',
    'miras'
  ],
  mapping: {
    classics4: { standard: 'case-ct_capsule' },
    renegades: { standard: 'case-ct_capsule' },
    sierras: { standard: 'case-ct_capsule' },
    tempests: { standard: 'case-ct_capsule' },
    zephyrs: { standard: 'case-ct_capsule' },
    tokas: { standard: 'case-ct_capsule' },
    miras: { standard: 'case-ct_capsule' },
  },
}]

const activeCaseSKU = computed(() => props.customisedProduct.sku)

// Fetch product details for all available cases so we can redirect accordingly
const allOtherCaseSKUs = [
  ...new Set(allCaseOptions
  .flatMap(
    (caseOptions) => Object.values(caseOptions.mapping).flatMap(Object.values)
  ))
].filter((caseSKU) => caseSKU !== activeCaseSKU.value)
const allOtherCustomisedProductsArray = await Promise.all(
  allOtherCaseSKUs.map((caseSKU) => $product.getCustomisedProduct(caseSKU))
)
const allOtherCustomisedProducts = Object.fromEntries(
  allOtherCustomisedProductsArray.map((customisedProduct) => [
    customisedProduct.sku,
    customisedProduct
  ])
)

const caseOptions = computed(() => allCaseOptions.find((caseOptions) => {
  const caseSKUs = Object.values(caseOptions?.mapping || {})
    .map((cases) => Object.values(cases))
    .flat()
  return caseSKUs.includes(activeCaseSKU.value)
}))

const expectedCaseSKU = computed(() => {
  const cases = caseOptions.value?.mapping[chosenProduct.value]
  return cases?.[caseStyle.value]
})

watch(expectedCaseSKU, async (expectedCaseSKU) => {
  if (expectedCaseSKU && activeCaseSKU.value !== expectedCaseSKU) {
    await navigateTo({
      path: resolveLocalePath(allOtherCustomisedProducts[expectedCaseSKU].url)
    })
  }
})

Object.entries(caseOptions.value?.mapping || {}).forEach(([product, cases]) => {
  if (!Object.values(cases).includes(activeCaseSKU.value)) return

  chosenProduct.value = product

  const flippedCases = Object.fromEntries(
    Object.entries(cases).map((a) => a.reverse())
  )
  caseStyle.value = flippedCases[activeCaseSKU.value]
})
</script>

<style lang="postcss">
.CaseCustomiser {
  @apply flex flex-col
    space-y-sm
    mt-xs mb-sm;
}

.CaseCustomiser__product-select,
.CaseCustomiser__case-type-select {
  @apply grid grid-cols-2 gap-3;
}

.CaseCustomiser__product-icon {
  font-size: 2em;
}

.CaseCustomiser__product-name {
  @apply pt-xs;
}
</style>
